/**
 * Log Review findings list endpoints — the server-side paginated table
 * behind the three dashboard tabs, plus the single call that returns all
 * three tab count badges.
 */

import { LOG_REVIEW_URL } from "../constants/index";
import type {
  LogReviewRow,
  LogReviewServerNode,
  LogReviewSessionDetail,
  LogReviewStatusTab,
} from "../app/types/logReview";
import { logReviewPost, toNumber } from "./logReviewClient";

/**
 * Payload for the Non-Compliant findings list. Mirrors the top filter bar
 * (appCode / category / division / date window) merged with the table's
 * own search keyword and drawer facets (teams / risk / username).
 */
export interface LogReviewListParams {
  keyword: string;
  sortByColumn: string;
  sortOrder: "asc" | "desc" | "";
  appCode: string[];
  category: string[];
  teams: string[];
  division: string[];
  username: string[];
  risk: string[];
  status: string[];
  startDate: string;
  endDate: string;
}

export interface LogReviewListResult {
  rows: LogReviewRow[];
  totalCount: number;
}

interface RawFinding {
  id: number;
  changeRequestId: string;
  application: string;
  category: string;
  team: string[] | null;
  plannedStartTime: string | null;
  plannedEndTime: string | null;
  actualStartTime: string | null;
  actualEndTime: string | null;
  reasonForNonCompliance: string[] | null;
  servers: number | null;
  status: string;
  users: string[] | null;
  division: string | null;
  applicationOwner: string | null;
  risk: string | null;
}

interface RawListResponse {
  data: RawFinding[];
  totalCount: number;
}

const join = (arr: string[] | null | undefined): string => (arr ?? []).join(", ");

// Risk arrives as "High - Some service degradation possible" — keep the
// severity prefix only for the Risk badge.
const parseRisk = (raw: string | null): LogReviewRow["risk"] => {
  const s = (raw ?? "").toLowerCase();
  if (s.startsWith("high")) return "High";
  if (s.startsWith("medium")) return "Medium";
  return "Low";
};

const toLogReviewRow = (raw: RawFinding): LogReviewRow => {
  const teams = raw.team ?? [];
  const serverNodes: LogReviewServerNode[] = teams.map((team) => ({
    team,
    serverCis: `${raw.servers ?? 0}`,
  }));
  // Date/time strings are passed through exactly as the backend formats
  // them (`DD/MM/YYYY, HH:mm`). The frontend does not reparse or reformat
  // date fields — doing so previously round-tripped through a Date and
  // re-rendered them in 12-hour AM/PM, which the backend never sent.
  const plannedStart = raw.plannedStartTime ?? "";
  const plannedEnd = raw.plannedEndTime ?? "";
  const actualStart = raw.actualStartTime ?? "";
  const actualEnd = raw.actualEndTime ?? "";
  const sessionDetails: LogReviewSessionDetail[] = [
    {
      lanUserId: join(raw.users),
      serverNode: `${raw.servers ?? 0}`,
      targetAccount: "—",
      plannedStart,
      plannedEnd,
      actualStart,
      actualEnd,
      status: raw.status,
    },
  ];
  return {
    id: `${raw.id}`,
    changeRequestId: raw.changeRequestId,
    application: raw.application,
    division: raw.division ?? "",
    userName: join(raw.users),
    category: raw.category,
    team: join(raw.team),
    teamLead: raw.applicationOwner ?? "",
    risk: parseRisk(raw.risk),
    plannedStart,
    plannedEnd,
    actualStart,
    actualEnd,
    servers: `${raw.servers ?? 0}`,
    status: raw.status,
    reasonForNonCompliance: join(raw.reasonForNonCompliance),
    serverNodes,
    sessionDetails,
    justificationHistory: [],
  };
};

// Shared server-side list fetch. Per the backend URL convention the path
// order is `{pageNumber}/{itemsPerPage}` — pageNumber first.
const fetchFindingsList = async (
  endpoint: string,
  params: LogReviewListParams,
  pageNumber: number,
  itemsPerPage: number,
  signal?: AbortSignal,
): Promise<LogReviewListResult> => {
  const raw = await logReviewPost<RawListResponse>(
    `${LOG_REVIEW_URL.SIRAR}/${endpoint}/${pageNumber}/${itemsPerPage}`,
    params,
    signal,
  );
  return {
    rows: (raw.data ?? []).map(toLogReviewRow),
    totalCount: raw.totalCount ?? 0,
  };
};

/** Non-Compliant findings list (server-side paginated). */
export const fetchNonCompliantFindingsList = (
  params: LogReviewListParams,
  pageNumber = 0,
  itemsPerPage = 10,
  signal?: AbortSignal,
): Promise<LogReviewListResult> =>
  fetchFindingsList("getNonCompliantFindingsList", params, pageNumber, itemsPerPage, signal);

/** Non-Compliant (Acknowledged) findings list (server-side paginated). */
export const fetchNonCompliantAcknowledgeFindingsList = (
  params: LogReviewListParams,
  pageNumber = 0,
  itemsPerPage = 10,
  signal?: AbortSignal,
): Promise<LogReviewListResult> =>
  fetchFindingsList(
    "getNonCompliantAcknowledgeFindingsList",
    params,
    pageNumber,
    itemsPerPage,
    signal,
  );

/** Compliant findings list (server-side paginated). */
export const fetchCompliantFindingsList = (
  params: LogReviewListParams,
  pageNumber = 0,
  itemsPerPage = 10,
  signal?: AbortSignal,
): Promise<LogReviewListResult> =>
  fetchFindingsList("getCompliantFindingsList", params, pageNumber, itemsPerPage, signal);

/** Tab id → list fetcher, so the table can switch tabs without a branch. */
export const FINDINGS_LIST_FETCHERS: Record<
  LogReviewStatusTab,
  (
    params: LogReviewListParams,
    pageNumber?: number,
    itemsPerPage?: number,
    signal?: AbortSignal,
  ) => Promise<LogReviewListResult>
> = {
  NonCompliantFindings: fetchNonCompliantFindingsList,
  NonCompliantAcknowledgedFindings: fetchNonCompliantAcknowledgeFindingsList,
  CompliantFindings: fetchCompliantFindingsList,
};

// ─── Tab counts (one call returns all three tab totals) ──────────────────

/** Per-tab finding totals shown as badges on the table tabs. */
export type FindingsTabCount = Record<LogReviewStatusTab, number>;

interface RawTabCountResponse {
  status?: string;
  data?: Array<{ status: string; count: number | string }>;
}

// Backend status label → tab id. Matched case-insensitively so minor casing
// drift ("Non-Compliance" vs "Non-compliance") still maps.
const TAB_BY_STATUS: Record<string, LogReviewStatusTab> = {
  "non-compliance findings": "NonCompliantFindings",
  "non-compliance acknowledged findings": "NonCompliantAcknowledgedFindings",
  "compliance findings": "CompliantFindings",
};

/**
 * Single-call totals for the three findings tabs. Shares the list filter
 * payload (minus pagination / the per-tab status facet, which the breakdown
 * itself represents).
 */
export const fetchFindingsTabCount = async (
  params: Omit<LogReviewListParams, "sortByColumn" | "sortOrder">,
  signal?: AbortSignal,
): Promise<FindingsTabCount> => {
  const body = {
    keyword: params.keyword,
    appCode: params.appCode,
    category: params.category,
    teams: params.teams,
    division: params.division,
    username: params.username,
    risk: params.risk,
    startDate: params.startDate,
    endDate: params.endDate,
  };
  const raw = await logReviewPost<RawTabCountResponse>(
    `${LOG_REVIEW_URL.SIRAR}/getFindingsTabCount`,
    body,
    signal,
  );
  const counts: FindingsTabCount = {
    NonCompliantFindings: 0,
    NonCompliantAcknowledgedFindings: 0,
    CompliantFindings: 0,
  };
  (raw.data ?? []).forEach((d) => {
    const tab = TAB_BY_STATUS[(d.status ?? "").trim().toLowerCase()];
    if (tab) counts[tab] = toNumber(d.count);
  });
  return counts;
};
